import { useEffect, useMemo, useRef, useState } from "react";
import { parseISODate, toISODate } from "./DatePicker";

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface RangeDatePickerProps {
  start: string;
  end: string;
  onStartChange: (iso: string) => void;
  onEndChange: (iso: string) => void;
  min?: string;
  max?: string;
  startUnavailable?: Set<string>;
  startTurnover?: Set<string>;
  endMin?: string;
  endMax?: string;
  endUnavailable?: Set<string>;
  endTurnover?: Set<string>;
  turnoverTooltip?: string;
  isDe?: boolean;
  startLabel?: string;
  endLabel?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
}

export default function RangeDatePicker({
  start,
  end,
  onStartChange,
  onEndChange,
  min,
  max,
  startUnavailable,
  startTurnover,
  endMin,
  endMax,
  endUnavailable,
  endTurnover,
  turnoverTooltip,
  isDe = true,
  startLabel,
  endLabel,
  startPlaceholder,
  endPlaceholder,
}: RangeDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<string | null>(null);

  const initial = parseISODate(start) ?? parseISODate(min ?? "") ?? new Date();
  const [view, setView] = useState({ year: initial.getFullYear(), month: initial.getMonth() });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const emptySet = useMemo(() => new Set<string>(), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHover(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Follow the selected start date so the departure day is reachable on the same calendar.
  useEffect(() => {
    const s = parseISODate(start);
    if (s) setView({ year: s.getFullYear(), month: s.getMonth() });
  }, [start]);

  const months = isDe ? MONTHS_DE : MONTHS_EN;
  const weekdays = isDe ? WEEKDAYS_DE : WEEKDAYS_EN;

  const format = (iso: string) => {
    const d = parseISODate(iso);
    if (!d) return "";
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Phase: are we picking the start (arrival) or the end (departure)?
  const selectingStart = !start || (start && end);

  const minDate = selectingStart ? parseISODate(min ?? "") : parseISODate(endMin ?? "");
  const maxDate = selectingStart ? parseISODate(max ?? "") : parseISODate(endMax ?? "");
  const unavailable = selectingStart ? startUnavailable ?? emptySet : endUnavailable ?? emptySet;
  const turnover = selectingStart ? startTurnover ?? emptySet : endTurnover ?? emptySet;

  const startD = parseISODate(start);
  const endD = parseISODate(end);
  const hoverD = hover ? parseISODate(hover) : null;
  const rangeEnd = endD ?? hoverD;

  const buildCells = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const offset = (firstDay + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: {
      day: number | null;
      iso: string | null;
      outOfRange: boolean;
      unavailable: boolean;
      isStart: boolean;
      isEnd: boolean;
      inRange: boolean;
      isTurnover: boolean;
    }[] = [];

    for (let i = 0; i < offset; i++) {
      result.push({
        day: null,
        iso: null,
        outOfRange: false,
        unavailable: false,
        isStart: false,
        isEnd: false,
        inRange: false,
        isTurnover: false,
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = toISODate(date);
      let outOfRange = false;
      if (minDate && date < minDate) outOfRange = true;
      if (maxDate && date > maxDate) outOfRange = true;
      const isUnavailable = unavailable.has(iso);
      const isStart = iso === start;
      const isEnd = iso === end;
      const inRange =
        !!startD && !!rangeEnd && date > startD && date < rangeEnd && !isStart && !isEnd;
      const isTurnover = turnover.has(iso);
      result.push({
        day: d,
        iso,
        outOfRange,
        unavailable: isUnavailable,
        isStart,
        isEnd,
        inRange,
        isTurnover,
      });
    }
    while (result.length % 7 !== 0) {
      result.push({
        day: null,
        iso: null,
        outOfRange: false,
        unavailable: false,
        isStart: false,
        isEnd: false,
        inRange: false,
        isTurnover: false,
      });
    }
    return result;
  };

  const leftCells = useMemo(
    () => buildCells(view.year, view.month),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, minDate, maxDate, unavailable, turnover, start, end, hover, startD, rangeEnd]
  );
  const rightMonth = new Date(view.year, view.month + 1, 1);
  const rightCells = useMemo(
    () => buildCells(rightMonth.getFullYear(), rightMonth.getMonth()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, minDate, maxDate, unavailable, turnover, start, end, hover, startD, rangeEnd]
  );

  const prevMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month - 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const nextMonth = () =>
    setView((v) => {
      const d = new Date(v.year, v.month + 1, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });

  const handleSelect = (iso: string, disabled: boolean) => {
    if (disabled || !iso) return;
    if (selectingStart) {
      onStartChange(iso);
      onEndChange("");
      setHover(null);
    } else {
      if (iso === start) return;
      const s = parseISODate(start);
      const d = parseISODate(iso);
      if (s && d && d < s) {
        onStartChange(iso);
        onEndChange("");
        setHover(null);
      } else {
        onEndChange(iso);
        setHover(null);
        setOpen(false);
      }
    }
  };

  const renderMonth = (monthYear: { year: number; month: number }, cells: typeof leftCells) => (
    <div className="flex-1 min-w-[264px]">
      <div className="text-center font-heading text-foreground-950 mb-3">
        {months[monthYear.month]} {monthYear.year}
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((w) => (
          <div key={w} className="text-center text-xs font-medium text-foreground-600 py-1">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (c.day === null) {
            return <div key={i} className="aspect-square"></div>;
          }
          const disabled = c.outOfRange || c.unavailable;
          const selected = c.isStart || c.isEnd;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => c.iso && handleSelect(c.iso, disabled)}
              onMouseEnter={() => {
                if (!disabled && !selectingStart) setHover(c.iso);
              }}
              onMouseLeave={() => setHover(null)}
              title={c.isTurnover ? turnoverTooltip : undefined}
              className={`aspect-square rounded-md text-sm font-medium transition-colors relative ${
                c.unavailable
                  ? "bg-red-100/50 text-red-700 cursor-not-allowed"
                  : c.outOfRange
                  ? "text-foreground-300 cursor-not-allowed"
                  : selected
                  ? "bg-primary-600 text-background-50 cursor-pointer"
                  : c.inRange
                  ? "bg-primary-100 text-primary-900 cursor-pointer hover:bg-primary-200"
                  : c.isTurnover
                  ? "border border-accent-700 text-foreground-900 cursor-pointer hover:bg-accent-50"
                  : "hover:bg-accent-100 text-foreground-800 cursor-pointer"
              }`}
            >
              {c.day}
              {c.isTurnover && !selected && (
                <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-accent-500" />
              )}
              {c.isTurnover && selected && (
                <span className="absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-background-50" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors cursor-pointer"
      >
        <div className="flex-1 text-left">
          <span className="block text-xs text-foreground-500 mb-0.5">
            {startLabel || (isDe ? "Anreise" : "Check-in")}
          </span>
          <span className={start ? "text-foreground-950" : "text-foreground-400"}>
            {start ? format(start) : startPlaceholder || (isDe ? "Datum wählen" : "Select date")}
          </span>
        </div>
        <i className="ri-arrow-right-line text-foreground-400"></i>
        <div className="flex-1 text-left">
          <span className="block text-xs text-foreground-500 mb-0.5">
            {endLabel || (isDe ? "Abreise" : "Check-out")}
          </span>
          <span className={end ? "text-foreground-950" : "text-foreground-400"}>
            {end ? format(end) : endPlaceholder || (isDe ? "Datum wählen" : "Select date")}
          </span>
        </div>
        <i className="ri-calendar-line text-foreground-500"></i>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 bg-background-50 border border-background-200 rounded-lg p-4 w-[640px] max-w-[92vw]">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <div className="text-xs text-foreground-500">
              {selectingStart
                ? isDe
                  ? "Anreisedatum wählen"
                  : "Select check-in date"
                : isDe
                ? "Abreisedatum wählen"
                : "Select check-out date"}
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-background-200 cursor-pointer text-foreground-800"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            {renderMonth(view, leftCells)}
            {renderMonth(
              { year: rightMonth.getFullYear(), month: rightMonth.getMonth() },
              rightCells
            )}
          </div>
        </div>
      )}
    </div>
  );
}